import type {
  Agg,
  ColumnType,
  DatasetColumn,
  DatasetManifestEntry,
  DateGranularity,
  FilterOp,
  GroupBy,
  Measure,
  ReportDefinition,
  ReportFilter,
  ReportView,
} from '../types/reporting';
import { dimAlias, measureAlias } from './compileReport';
import {
  NO_RELATED,
  referencedRelatedKeys,
  resolveColumnMeta,
  splitRef,
  type RelatedManifests,
} from './columnRefs';

// Aggregations other than count operate on a numeric column, so they only make
// sense when the dataset has at least one number column to aggregate.
const NON_COUNT_AGGS: Agg[] = ['sum', 'avg', 'min', 'max'];

export function numericColumns(dataset: DatasetManifestEntry): DatasetColumn[] {
  return dataset.columns.filter(c => c.type === 'number');
}

export function availableAggs(dataset: DatasetManifestEntry): Agg[] {
  return numericColumns(dataset).length > 0
    ? ['count', ...NON_COUNT_AGGS]
    : ['count'];
}

// Drop any sort key that targets `alias`. Used when the group-by or measure that
// produced that alias is removed or renamed, so `definition.sort` never carries
// a reference to an output column the query no longer emits. compileReport's
// orderClause already ignores an unknown sort target at query time, but leaving
// it in the canonical definition would let it resurface (e.g. re-add the same
// measure) and get persisted in saved reports.
function dropSortBy(def: ReportDefinition, alias: string): ReportDefinition {
  if (!def.sort.some(s => s.by === alias)) return def;
  return { ...def, sort: def.sort.filter(s => s.by !== alias) };
}

function defaultGroupBy(
  column: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): GroupBy {
  const meta = resolveColumnMeta(column, dataset, related);
  if (meta?.type === 'number' && meta.default_buckets?.length) {
    return {
      column,
      transform: { type: 'bucket', edges: [...meta.default_buckets] },
    };
  }
  if (meta?.type === 'date') {
    return {
      column,
      transform: {
        type: 'date_trunc',
        unit: meta.default_granularity ?? 'month',
      },
    };
  }
  return { column };
}

export function toggleDetailColumn(
  def: ReportDefinition,
  column: string,
  dataset: DatasetManifestEntry
): ReportDefinition {
  // Empty columns means "all columns selected" (the canonical form).
  const all = dataset.columns.map(c => c.name);
  const current = def.columns.length === 0 ? all : def.columns;
  const removing = current.includes(column);
  const next = removing
    ? current.filter(c => c !== column)
    : [...current, column];
  // Canonicalize to [] ("all columns, in dataset order") ONLY on an EXACT
  // positional match to dataset order. A set-equality check (order-independent)
  // would silently collapse a user's explicit reorderColumns order the moment
  // every column is present again, discarding the very order that feature saves.
  // With positional matching a re-added column lands at the end and stays an
  // explicit array; only re-adding it into its original slot re-canonicalizes.
  const isAll =
    next.length === all.length && next.every((c, i) => c === all[i]);
  const withColumns = { ...def, columns: isAll ? [] : next };
  // Removing a detail column that is an active sort key strands that key: the
  // detail column name IS its sort `by`, so compileReport's orderClause silently
  // drops it from the query while definition.sort keeps a phantom entry that
  // breaks the sidebar Sort row and persists into saved reports. Mirror
  // removeGroupBy/removeMeasure and clear it.
  return removing ? dropSortBy(withColumns, column) : withColumns;
}

// Reorder the DETAIL column order. The effective list is the emitted detail order
// — `def.columns` when non-empty, else `resultColumns` (the full primary-dataset
// order the compiler emits for the '[] = all' canonical form, matching
// compileReport's `def.columns.length > 0 ? def.columns : dataset.columns`).
// Moves fromIndex→toIndex within that list and persists the EXPLICIT ordered
// array. DECISION: unlike toggleDetailColumn, this does NOT re-canonicalize to []
// when all columns remain present — [] means "all in dataset order", a DIFFERENT
// order, so canonicalizing would silently discard the reorder that is the whole
// point of the feature. from===to or an out-of-range index returns def unchanged
// (identity), so a from===to call on an empty-columns def never spuriously
// materializes the explicit list. Round-trips because def.columns already saves.
export function reorderColumns(
  def: ReportDefinition,
  fromIndex: number,
  toIndex: number,
  resultColumns: string[]
): ReportDefinition {
  const current = def.columns.length > 0 ? def.columns : resultColumns;
  const n = current.length;
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    fromIndex >= n ||
    toIndex < 0 ||
    toIndex >= n
  ) {
    return def;
  }
  const next = [...current];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return { ...def, columns: next };
}

export function addGroupBy(
  def: ReportDefinition,
  column: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): ReportDefinition {
  // A group-by's output alias IS its column (dimAlias), so grouping by a column
  // that's already a dimension would compile two `... AS "<col>"` SELECT items —
  // duplicate result columns that surface as duplicate React table/chart keys
  // and duplicate CSV headers. Never introduce a duplicate dimension; if the
  // column is already grouped, leave the definition unchanged.
  if (def.group_bys.some(g => dimAlias(g) === column)) return def;
  return {
    ...def,
    group_bys: [...def.group_bys, defaultGroupBy(column, dataset, related)],
  };
}

export function removeGroupBy(
  def: ReportDefinition,
  index: number
): ReportDefinition {
  const removed = def.group_bys[index];
  let next = {
    ...def,
    group_bys: def.group_bys.filter((_, i) => i !== index),
  };
  // Subtotals summarize runs of the leading dimension within at least one
  // finer dimension, so they need >= 2 group-bys. Strip the flag (not just
  // stop rendering) when the definition drops below that: a dormant true
  // would silently reactivate when an unrelated group-by is added later.
  if (next.group_bys.length < 2) next = stripSubtotals(next);
  return removed ? dropSortBy(next, dimAlias(removed)) : next;
}

// Remove the optional subtotals key entirely (rather than store false) so a
// disabled state round-trips identically to a legacy definition.
function stripSubtotals(def: ReportDefinition): ReportDefinition {
  if (!('subtotals' in def)) return def;
  const next = { ...def };
  delete next.subtotals;
  return next;
}

export function setSubtotals(
  def: ReportDefinition,
  enabled: boolean
): ReportDefinition {
  return enabled ? { ...def, subtotals: true } : stripSubtotals(def);
}

export function setGroupByColumn(
  def: ReportDefinition,
  index: number,
  column: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): ReportDefinition {
  // Switching this dimension onto a column another dimension already groups by
  // would collide their aliases (see addGroupBy). Leave the definition unchanged
  // rather than emit two identically named result columns.
  if (def.group_bys.some((g, i) => i !== index && dimAlias(g) === column)) {
    return def;
  }
  const previous = def.group_bys[index];
  const group_bys = def.group_bys.map((g, i) =>
    i === index ? defaultGroupBy(column, dataset, related) : g
  );
  const next = { ...def, group_bys };
  // The dimension's alias IS its column; moving it to a new column strands any
  // sort that targeted the old one.
  return previous && dimAlias(previous) !== column
    ? dropSortBy(next, dimAlias(previous))
    : next;
}

function updateTransform(
  def: ReportDefinition,
  index: number,
  transform: GroupBy['transform']
): ReportDefinition {
  const group_bys = def.group_bys.map((g, i) =>
    i === index
      ? transform
        ? { column: g.column, transform }
        : { column: g.column }
      : g
  );
  return { ...def, group_bys };
}

export function setBucketEdges(
  def: ReportDefinition,
  index: number,
  edges: number[]
): ReportDefinition {
  return updateTransform(
    def,
    index,
    edges.length > 0 ? { type: 'bucket', edges } : undefined
  );
}

export function setDateGranularity(
  def: ReportDefinition,
  index: number,
  unit: DateGranularity | null
): ReportDefinition {
  return updateTransform(
    def,
    index,
    unit ? { type: 'date_trunc', unit } : undefined
  );
}

export function addMeasure(
  def: ReportDefinition,
  dataset: DatasetManifestEntry
): ReportDefinition {
  // "Add measure" must introduce a NEW output column, never a duplicate. Two
  // measures that resolve to the same alias (a second Count, or a second
  // sum(charge)) compile to one column at query time — so blindly appending
  // another Count would desync what the builder displays and saves from what
  // the query actually returns (and reopening the report would silently drop
  // the duplicate). Add the first candidate — Count, then each aggregate over
  // each numeric column, in menu order — whose alias isn't already present. If
  // every candidate is taken, leave the definition unchanged.
  const existing = new Set(def.measures.map(measureAlias));
  const candidates: Measure[] = [
    { agg: 'count' },
    ...numericColumns(dataset).flatMap(c =>
      NON_COUNT_AGGS.map((agg): Measure => ({ agg, column: c.name }))
    ),
  ];
  const next = candidates.find(m => !existing.has(measureAlias(m)));
  return next ? { ...def, measures: [...def.measures, next] } : def;
}

export function updateMeasure(
  def: ReportDefinition,
  index: number,
  measure: Measure
): ReportDefinition {
  // Two measures that resolve to the same alias (same agg+column, e.g. a second
  // Count or a second sum(charge)) compile to ONE output column, desyncing what
  // the builder displays and saves from what the query returns (reopening then
  // silently drops the duplicate). Reject an edit that would collide with another
  // measure's alias so the canonical definition never holds a duplicate.
  const alias = measureAlias(measure);
  if (def.measures.some((m, i) => i !== index && measureAlias(m) === alias)) {
    return def;
  }
  const previous = def.measures[index];
  const next = {
    ...def,
    measures: def.measures.map((m, i) => (i === index ? measure : m)),
  };
  // Changing the aggregation or column changes the measure's output alias, so a
  // sort that targeted the old alias ("sum_balance" → "avg_balance") is stale.
  return previous && measureAlias(previous) !== alias
    ? dropSortBy(next, measureAlias(previous))
    : next;
}

export function setMeasureAgg(
  def: ReportDefinition,
  index: number,
  agg: Agg,
  dataset: DatasetManifestEntry
): ReportDefinition {
  if (agg === 'count') return updateMeasure(def, index, { agg: 'count' });
  const numeric = numericColumns(dataset);
  // Aliases already taken by the OTHER measures — the new aggregate must not
  // collide with one (see updateMeasure), or it would compile to a duplicate
  // column and desync the definition.
  const taken = new Set(
    def.measures.filter((_, i) => i !== index).map(measureAlias)
  );
  const existing = def.measures[index]?.column;
  // Prefer keeping the current column when it's numeric; otherwise snap to a
  // numeric column. In either case skip any candidate whose resulting alias is
  // already taken by another measure.
  const preferred =
    existing && numeric.some(c => c.name === existing) ? [existing] : [];
  const column = [...preferred, ...numeric.map(c => c.name)].find(
    name => !taken.has(measureAlias({ agg, column: name }))
  );
  // No numeric column to aggregate, or every aggregate over one is already
  // present — leave the measure untouched rather than create an invalid or
  // duplicate one.
  if (!column) return def;
  return updateMeasure(def, index, { agg, column });
}

export function removeMeasure(
  def: ReportDefinition,
  index: number
): ReportDefinition {
  const removed = def.measures[index];
  const next = {
    ...def,
    measures: def.measures.filter((_, i) => i !== index),
  };
  return removed ? dropSortBy(next, measureAlias(removed)) : next;
}

export function setPivotColumn(
  def: ReportDefinition,
  column: string | null,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): ReportDefinition {
  if (column === null) return { ...def, pivot_column: null };
  const meta = resolveColumnMeta(column, dataset, related);
  // Pivot values-to-columns works best on plain (untransformed) dimensions,
  // except dates which pivot by month by default.
  const pivot: GroupBy =
    meta?.type === 'date'
      ? { column, transform: { type: 'date_trunc', unit: 'month' } }
      : { column };
  // In a pivot ONLY the row-dimension aliases are addressable ORDER BY targets:
  // measures become dynamically generated value columns and detail columns are
  // not emitted at all. Keep only sorts targeting a current group-by dimension,
  // so the canonical definition never holds a sort the pivoted query can't honor
  // — a stale measure-alias sort OR a leftover detail-column sort (e.g.
  // {by: 'balance'}) would otherwise persist and resurface when the pivot is
  // later removed.
  const dimAliases = new Set(def.group_bys.map(dimAlias));
  return {
    ...def,
    pivot_column: pivot,
    sort: def.sort.filter(s => dimAliases.has(s.by)),
  };
}

export function setView(
  def: ReportDefinition,
  view: ReportView
): ReportDefinition {
  return { ...def, view };
}

// Cycle ONE column within the ordered multi-sort for the given column: absent →
// append ascending → descending (in place) → removed. Cycling a column never
// touches the OTHER sort keys or their precedence, so a table-header click stays
// in lockstep with the sidebar's Sort list (both mutate `definition.sort`). The
// result feeds `definition.sort`, so DuckDB owns the actual ordering via the
// compiled ORDER BY (which emits every applicable key) — the table never sorts
// rows in JavaScript.
export function cycleSort(
  def: ReportDefinition,
  column: string
): ReportDefinition {
  const index = def.sort.findIndex(s => s.by === column);
  if (index === -1) {
    // Absent → append a new ascending key, preserving the existing keys' order.
    return { ...def, sort: [...def.sort, { by: column, dir: 'asc' }] };
  }
  if (def.sort[index].dir === 'asc') {
    // asc → desc in place; other keys untouched.
    return {
      ...def,
      sort: def.sort.map((s, i) => (i === index ? { ...s, dir: 'desc' } : s)),
    };
  }
  // desc → remove this key; the remaining keys keep their precedence.
  return { ...def, sort: def.sort.filter((_, i) => i !== index) };
}

// --- Ordered multi-sort helpers (sidebar Sort section) --------------------
// definition.sort is an ordered {by,dir}[] and compileReport's orderClause emits
// every applicable key as an ORDER BY term, so these are thin immutable array
// ops. Each preserves the no-duplicate-by-key invariant (orderClause would emit
// `ORDER BY x, x` for a repeated target). All out-of-range / no-op calls return
// `def` unchanged so callers keep identity-based idempotency.

// Append { by: column, dir: 'asc' } if the column isn't already sorted; no-op
// (returns def unchanged) if it is.
export function addSort(
  def: ReportDefinition,
  column: string
): ReportDefinition {
  if (def.sort.some(s => s.by === column)) return def;
  return { ...def, sort: [...def.sort, { by: column, dir: 'asc' }] };
}

// Remove the sort key at `index`; out-of-range index returns def unchanged.
export function removeSort(
  def: ReportDefinition,
  index: number
): ReportDefinition {
  if (index < 0 || index >= def.sort.length) return def;
  return { ...def, sort: def.sort.filter((_, i) => i !== index) };
}

// Set the direction of the sort key at `index`; out-of-range index returns def
// unchanged.
export function setSortDir(
  def: ReportDefinition,
  index: number,
  dir: 'asc' | 'desc'
): ReportDefinition {
  if (index < 0 || index >= def.sort.length) return def;
  return {
    ...def,
    sort: def.sort.map((s, i) => (i === index ? { ...s, dir } : s)),
  };
}

// Point the sort row at `index` to `column`, preserving its dir. Collision-
// guarded: if another row already sorts by `column`, returns def unchanged
// (never emits ORDER BY x, x). Out-of-range index returns def unchanged.
export function setSortColumn(
  def: ReportDefinition,
  index: number,
  column: string
): ReportDefinition {
  if (index < 0 || index >= def.sort.length) return def;
  if (def.sort.some((s, i) => i !== index && s.by === column)) return def;
  return {
    ...def,
    sort: def.sort.map((s, i) => (i === index ? { ...s, by: column } : s)),
  };
}

// Reorder sort precedence (primary, secondary, …): array move from→to. from===to
// or an out-of-range index returns def unchanged.
export function moveSort(
  def: ReportDefinition,
  from: number,
  to: number
): ReportDefinition {
  const n = def.sort.length;
  if (from === to || from < 0 || from >= n || to < 0 || to >= n) return def;
  const next = [...def.sort];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return { ...def, sort: next };
}

export function parseEdges(text: string): number[] {
  const values = text
    .split(',')
    .map(part => Number(part.trim()))
    .filter(n => Number.isFinite(n) && n > 0);
  return [...new Set(values)].sort((a, b) => a - b);
}

export function addFilter(
  def: ReportDefinition,
  column: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): ReportDefinition {
  const meta = resolveColumnMeta(column, dataset, related);
  let filter: ReportFilter;
  switch (meta?.type) {
    case 'number':
      filter = { column, op: 'gt', value: 0 };
      break;
    case 'date':
      filter = { column, op: 'relative_date', value: { last_days: 30 } };
      break;
    case 'boolean':
      filter = { column, op: 'eq', value: true };
      break;
    default:
      filter = { column, op: 'contains', value: '' };
  }
  return { ...def, filters: [...def.filters, filter] };
}

// The initial value the editor uses when a filter's column or operator changes.
// Both between bounds start empty (never [0, 0]) so a freshly selected between —
// date or number — is incomplete and stays inactive until the user supplies
// both bounds, rather than immediately applying BETWEEN 0 AND 0.
export function defaultValueFor(op: FilterOp, type: ColumnType): unknown {
  if (op === 'relative_date') return { last_days: 30 };
  if (op === 'between') return ['', ''];
  if (op === 'in') return [];
  if (op === 'is_null' || op === 'not_null') return undefined;
  if (type === 'number') return 0;
  if (type === 'boolean') return true;
  return '';
}

export function updateFilter(
  def: ReportDefinition,
  index: number,
  filter: ReportFilter
): ReportDefinition {
  return {
    ...def,
    filters: def.filters.map((f, i) => (i === index ? filter : f)),
  };
}

export function removeFilter(
  def: ReportDefinition,
  index: number
): ReportDefinition {
  return { ...def, filters: def.filters.filter((_, i) => i !== index) };
}

// --- Joined datasets (explicit dataset-level joins) -----------------------

// Add a related dataset as an explicit join (mirrors "Add filter"). A dataset
// is joinable only when it is a declared many-to-one relationship target of the
// base AND the caller can access it — `related` is exactly that
// permission-filtered set (relatedDatasetsFor), and the base's declared edges
// gate it structurally too. Adding is idempotent (a dataset already joined is a
// no-op) and append-only, so joined_datasets preserves the order the user
// joined datasets in — the order the compiler LEFT JOINs them.
export function addJoinedDataset(
  def: ReportDefinition,
  key: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): ReportDefinition {
  const declared = (dataset.relationships ?? []).some(
    r => r.target === key && r.cardinality === 'many_to_one'
  );
  if (!declared || !related.has(key)) return def;
  // Backfill first so the starting list already carries any dataset the
  // definition references via legacy dotted refs. Reading def.joined_datasets
  // raw would, on a not-yet-backfilled legacy def, treat the absent list as []
  // and silently drop those referenced datasets — correctness no longer depends
  // on an unenforced "backfill on load first" call order (codex Finding 2).
  const base = backfillJoinedDatasets(def, related);
  const current = base.joined_datasets ?? [];
  if (current.includes(key)) return base;
  return { ...base, joined_datasets: [...current, key] };
}

// Remove a joined dataset AND every clause that references it, mirroring
// removeGroupBy's stale-state cleanup: a column/group-by/pivot/filter/measure/
// sort that names a "<key>.<column>" ref (or, for sort, an alias that IS such a
// ref) would otherwise dangle after the join is gone — the compiler drops it
// but the canonical definition would still carry (and persist) it, and it would
// resurface if the same dataset were re-joined. Measures aggregate primary
// columns only in v1, so none reference a related dataset; the filter is kept
// for defense-in-depth and forward-compatibility.
export function removeJoinedDataset(
  def: ReportDefinition,
  key: string
): ReportDefinition {
  const refsKey = (ref: string): boolean => splitRef(ref).dataset === key;
  // A `chart` block pointing at the removed dataset must go WITH the join, not
  // wait for a reopen: removing the dataset strips its `columns` entries, so the
  // chart's refs are instantly stale, but validateDefinition (which drops a stale
  // chart) only runs on open — until then the Chart tab would stay live over a
  // dataset that is no longer in the FROM clause.
  const chartRefsKey =
    !!def.chart &&
    (refsKey(def.chart.group_by) ||
      (!!def.chart.column && refsKey(def.chart.column)));
  const next: ReportDefinition = {
    ...def,
    joined_datasets: (def.joined_datasets ?? []).filter(k => k !== key),
    columns: def.columns.filter(ref => !refsKey(ref)),
    group_bys: def.group_bys.filter(g => !refsKey(g.column)),
    measures: def.measures.filter(m => !(m.column && refsKey(m.column))),
    pivot_column:
      def.pivot_column && refsKey(def.pivot_column.column)
        ? null
        : def.pivot_column,
    filters: def.filters.filter(f => !refsKey(f.column)),
    sort: def.sort.filter(s => !refsKey(s.by)),
  };
  if (chartRefsKey) delete next.chart;
  return next;
}

// Load-time back-compat AND validate-and-degrade. `related` is the caller's
// permission-filtered declared many-to-one set (relatedDatasetsFor), so
// intersecting with it is the degrade: a dotted ref to a since-removed,
// undeclared, or newly-unpermitted dataset never fabricates a join (codex
// Finding 4), independent of whether validateDefinition has stripped that ref
// yet. Two cases:
//   - joined_datasets absent (legacy per-column UI): derive it from the
//     permitted datasets the dotted refs reference — order-stable (sorted,
//     matching the compiler's canonical join order).
//   - joined_datasets present: drop any entry no longer permitted, then union
//     in any permitted referenced dataset not yet listed, so joined_datasets is
//     always a superset of the datasets the dotted refs pull. This reconciles an
//     inconsistent `[] + episodes.soc_date` state (a compiler-visible dangling
//     ref) into `['episodes']` instead of leaving the two to disagree (codex
//     Finding 2). Returns the same reference when nothing changes, so callers
//     keep identity-based idempotency.
export function backfillJoinedDatasets(
  def: ReportDefinition,
  related: RelatedManifests
): ReportDefinition {
  const referenced = [...referencedRelatedKeys(def)]
    .filter(k => related.has(k))
    .sort();
  if (def.joined_datasets === undefined) {
    return { ...def, joined_datasets: referenced };
  }
  const kept = def.joined_datasets.filter(k => related.has(k));
  const missing = referenced.filter(k => !kept.includes(k));
  const next = [...kept, ...missing];
  const unchanged =
    next.length === def.joined_datasets.length &&
    next.every((k, i) => k === def.joined_datasets![i]);
  return unchanged ? def : { ...def, joined_datasets: next };
}
